<template>
  <div>
    <block-item-card
      :title="$t('NII.Section.InternalThresholds.NiiLimits.Title')"
      :tooltip="$t('NII.Section.InternalThresholds.NiiLimits.Tooltip')"
      class="fill-height"
    >
      <div class="pa-4">
        <div
          v-if="niiInternalThresholdsData"
          ref="niiChart"
          style="height: 600px; width: 100%;"
        />
      </div>
    </block-item-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import API from '@/api/Api'
import { clone, toDecimalMark } from '@/lib/utils/helper'
import NIISummaryResource from '@/api/resources/risks/NIISummaryResource'

export default {
  name      : 'InternalThresholds',
  components: { BlockItemCard },
  mixins    : [chartsColorMixin],
  props     : {
  },
  data () {
    return {
      niiChart       : null,
      originalApiData: null

    }
  },
  dataStore: {
    niiInternalThresholdsData: 'NII.FindInternalThresholds',
    currentAnalysisId        : 'ActiveAnalysis.currentAnalysisId'
  },
  computed: {
    accountId () {
      return this.user?.selectedAccountId || null
    },
    chartData () {
      if (!this.niiInternalThresholdsData || !this.niiInternalThresholdsData?.Data || !this.niiInternalThresholdsData?.Data?.InternalThresholds) {
        return null
      }

      const internalData = this.niiInternalThresholdsData?.Data?.InternalThresholds || []

      // Extract categories and values from the API data
      const categories = (internalData || []).map(item => item.title || 'Unknown')
      const niiValues = (internalData || []).map(item => item.actualValue || 0).map(v => toDecimalMark(v, 1))

      // Threshold values from API data
      const thresholdValues = (internalData || []).map(item => item.thresholdValue || 0).map(v => toDecimalMark(v, 1))
      const actualValues = (internalData || []).map(item => item.actualValue || 0)

      // Calculate ghost bars - extend from actual value to min/max threshold
      const minThreshold = Math.min(...actualValues)
      const maxThreshold = Math.max(...actualValues)

      return {
        categories,
        niiValues,
        thresholdValues,
        minThreshold,
        maxThreshold
      }
    },
    niiChartOptions () {
      return {
        title  : { show: false },
        tooltip: {
          show       : true,
          trigger    : 'axis',
          axisPointer: {
            type: 'shadow'
          },
          backgroundColor: this.themeColorRGBA('synthesis-ui-grey-01', 0.9),
          borderColor    : this.themeColorRGBA('synthesis-ui-grey-01'),
          borderWidth    : 1,
          textStyle      : { color: '#FFFFFF' },
          formatter      : function (params) {
            if (params && params.length > 0) {
              // Find the main data series (NII Limits)
              const mainData = params.find(param => param.seriesName === 'NII Limits')
              if (mainData) {
                return `${mainData.name}<br/>Value: ${mainData.value}%`
              }
              // Fallback to first parameter if main series not found
              const data = params[0]
              return `${data.name}<br/>Value: ${data.value}%`
            }
            return ''
          }
        },
        grid: {
          left           : '0',
          right          : '5%',
          bottom         : '5%',
          top            : '5%',
          containLabel   : true,
          backgroundColor: this.themeColorRGBA('synthesis-brand-white')
        },
        xAxis: {
          type    : 'category',
          data    : this.chartData.categories,
          axisLine: {
            lineStyle: { color: this.themeColorRGBA('synthesis-ui-grey-02'), type: 'dashed' }
          },
          axisTick : { show: false },
          axisLabel: { show: false }
        },
        yAxis: {
          type     : 'value',
          min      : this.chartData.minThreshold,
          max      : this.chartData.maxThreshold,
          axisLine : { show: false },
          axisTick : { show: false },
          axisLabel: {
            show  : false,
            color : this.themeColorRGBA('synthesis-ui-grey-02'),
            data  : this.chartData.thresholdValues,
            margin: 10
          },
          splitLine: {
            show     : false,
            lineStyle: {
              color: this.themeColorRGBA('synthesis-ui-grey-02'),
              type : 'dashed'
            }
          },
          z: 1
        },
        series: [
          // Background bars (ghost bars) - extend from 0 to threshold boundaries
          {
            name    : 'Background',
            type    : 'bar',
            barWidth: '40%',
            data    : this.chartData.niiValues.map((value) => {
              // Background bar extends from 0 to the appropriate threshold boundary
              if (value >= 0) {
                // For positive values, background goes from 0 to max threshold
                return this.chartData.maxThreshold
              } else {
                // For negative values, background goes from 0 to min threshold
                return this.chartData.minThreshold
              }
            }),
            itemStyle: {
              color       : this.themeColorRGBA('synthesis-ui-grey-02', 0.3),
              borderRadius: [4, 4, 4, 4]
            },
            silent        : true,
            z             : 99,
            barGap        : '-100%',
            barCategoryGap: '20%'
          },
          // Actual value bars - start from 0
          {
            name     : 'NII Limits',
            type     : 'bar',
            barWidth : '40%',
            data     : this.chartData.niiValues,
            itemStyle: {
              color       : this.themeColorRGBA('synthesis-ui-yellow-03'),
              borderRadius: [4, 4, 4, 4]
            },
            label: {
              show     : true,
              position : 'outside',
              formatter: function (params) {
                return params.value + '%'
              },
              color        : this.themeColorRGBA('synthesis-ui-grey-01'),
              fontWeight   : 'bold',
              align        : 'center',
              verticalAlign: 'middle',
              distance     : 20
            },
            markLine: {
              silent: true,
              symbol: 'none',
              z     : 0, // Lower z-index to appear behind bars
              data  : this.chartData.thresholdValues.map(threshold => ({
                yAxis    : threshold,
                lineStyle: {
                  color: this.themeColorRGBA('synthesis-ui-grey-02'),
                  type : 'dashed',
                  width: 1
                },
                label: {
                  show     : true,
                  position : 'insideStartTop',
                  formatter: threshold + '%',
                  color    : this.themeColorRGBA('synthesis-brand-red'),
                  fontSize : 10,
                  offset   : [-5, 0]
                }
              }))
            },
            z             : 100,
            barGap        : '-100%',
            barCategoryGap: '20%'
          },
          // Category name boxes
          {
            name    : 'Category Names',
            type    : 'bar',
            barWidth: '40%',
            data    : this.chartData.niiValues.map((value, index) => ({
              value    : value >= 0 ? 0.1 : -0.1, // Small value to create a box
              itemStyle: {
                color       : this.themeColorRGBA('synthesis-ui-grey-02', 0.8),
                borderRadius: [2, 2, 2, 2]
              }
            })),
            label: {
              show     : true,
              // position: function (params) {
              //   return params.value >= 0 ? 'top' : 'bottom'
              // },
              formatter: function (params) {
                return this.chartData.categories[params.dataIndex]
              }.bind(this),
              color          : 'white',
              fontFamily     : 'Inter',
              letterSpacing  : 0,
              width          : 60,
              fontWeight     : 600,
              fontSize       : 10,
              align          : 'center',
              verticalAlign  : 'middle',
              backgroundColor: this.themeColorRGBA('synthesis-ui-blue-06', 0.8),
              borderColor    : this.themeColorRGBA('synthesis-ui-grey-06'),
              borderWidth    : 0,
              borderRadius   : 2,
              padding        : [4, 8, 4, 8],
              position       : 'outside',
              distance       : -15
              // gap            : 10
            },
            z             : 3,
            barGap        : '-100%',
            barCategoryGap: '20%'
          }
        ]
      }
    }
  },
  watch: {
    niiInternalThresholdsData: {
      handler (newData) {
        this.$nextTick(() => {
          if (this.niiChart) {
            if (newData?.Data?.InternalThresholds) {
              this.niiChart.setOption(this.niiChartOptions)
              this.drawThresholdLines()
            }
          }
        })
      },
      deep     : true,
      immediate: true
    },
    chartData: {
      handler () {
        this.$nextTick(() => {
          if (this.niiChart && this.chartData) {
            this.niiChart.setOption(this.niiChartOptions)
            this.drawThresholdLines()
          }
        })
      },
      deep: true
    }
  },
  activated () {
    this.$nextTick(() => {
      if (this.$refs.niiChart && !this.niiChart) {
        this.initCharts()
      } else if (this.niiChart) {
        // Re-initialize charts with current data
        this.niiChart.setOption(this.niiChartOptions)
        this.drawThresholdLines()
      }
    })
  },
  mounted () {
    this.$nextTick(() => {
      this.initCharts()
    })
  },
  beforeDestroy () {
    if (this.niiChart) {
      this.niiChart.dispose()
    }
    window.removeEventListener('resize', this.handleResize)
    this.$bus.$off('nii:period-changed', this.onPeriodChanged)
    this.$bus.$off('nii:cancel-assumptions-edit', this.getData)
  },
  created () {
    this.getData()
    this.$bus.$on('nii:period-changed', this.onPeriodChanged)
    this.$bus.$on('nii:cancel-assumptions-edit', this.getData)
  },
  methods: {
    getData () {
      API.Resource.NII.FindInternalThresholds(this.accountId, this.currentAnalysisId)
        .then(response => {
          this.handleResponse(response)
        })
        .catch(e => {
          this.handleResponse(e.response)
        })
    },
    handleResponse (response) {
      const data = API.responseData(response)?.findNiiAnalysis || null
      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        this.originalApiData = clone(data)

        const o = clone(data)

        const v = new NIISummaryResource(o)

        this.$set(this.niiInternalThresholdsData, 'Data', v)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },
    initCharts () {
      try {
        if (this.$refs.niiChart && !this.niiChart) {
          this.niiChart = echarts.init(this.$refs.niiChart)

          this.niiChart.setOption(this.niiChartOptions)

          if (this.niiInternalThresholdsData?.Data?.InternalThresholds) {
            this.drawThresholdLines()
          }

          window.addEventListener('resize', this.handleResize)
        }
      } catch (error) {
        // Error initializing charts
      }
    },
    drawThresholdLines () {
      if (!this.niiChart || !this.chartData.thresholdValues) return

      // Clear existing threshold lines
      this.niiChart.setOption({
        graphic: []
      })

      // Draw individual threshold lines
      const thresholdLines = this.chartData.thresholdValues.map((threshold, index) => {
        // Get the exact pixel position of the bar
        const barPixelPos = this.niiChart.convertToPixel('grid', [index, 0])
        const thresholdPixelPos = this.niiChart.convertToPixel('grid', [index, threshold])

        if (!barPixelPos || !thresholdPixelPos) return null

        // Calculate bar width in pixels
        const barWidth = this.niiChart.getWidth() * 0.4 / this.chartData.categories.length

        return {
          type : 'line',
          left : barPixelPos[0] - barWidth / 2,
          top  : thresholdPixelPos[1] - 2,
          shape: {
            x1: 0,
            y1: 0,
            x2: barWidth,
            y2: 0
          },
          style: {
            stroke   : this.themeColorRGBA('synthesis-brand-red'),
            lineDash : [5, 5],
            lineWidth: 1.5
          },
          z: 999
        }
      }).filter(line => line !== null)

      // Add the threshold lines to the chart
      this.niiChart.setOption({
        graphic: thresholdLines
      })
    },
    handleResize () {
      if (this.niiChart) {
        this.niiChart.resize()
        this.drawThresholdLines()
      }
    },
    refreshCharts () {
      if (this.niiInternalThresholdsData && this.niiChart) {
        this.niiChart.setOption(this.niiChartOptions)
        this.drawThresholdLines()
      }
    },
    onPeriodChanged (newPeriod) {
      // Recreate the resource with the new selectedPeriod from original API data
      if (this.originalApiData) {
        const o = clone(this.originalApiData)
        o.selectedPeriod = newPeriod
        const v = new NIISummaryResource(o)
        this.$set(this.niiInternalThresholdsData, 'Data', v)
      }
    }
  }
}
</script>

<style scoped>
</style>
